import { useCallback, useEffect, useMemo, useRef } from "preact/hooks";
import { AltHintTarget } from "../../components/AltHintTarget/AltHintTarget.jsx";
import { ControlBar } from "../../components/ControlBar/ControlBar.jsx";
import { Panel } from "../../components/Panel/Panel.jsx";
import { SegmentedControl } from "../../components/SegmentedControl/SegmentedControl.jsx";
import { formatCurrency } from "../../lib/formatters.js";
import { EXPENSE_TYPE_IRREGULAR, EXPENSE_TYPE_ONGOING, normalizeExpenseType } from "../../lib/expenseType.js";
import {
  setAnalysisChartCategory,
  setAnalysisChartSupplier,
  setBudgetSearchQuery,
  setExpensesSearchQuery
} from "../../lib/pageSearchState.js";
import { createScopeDateContext } from "../../lib/scopeDate.js";
import { createSectionNavigationBindings } from "../../lib/shortcuts/createSectionNavigationBindings.js";
import { onKey } from "../../lib/shortcuts/onKey.js";
import { useShortcutBindings } from "../../lib/shortcuts/useShortcutBindings.js";
import { usePageStateMemory } from "../../lib/usePageStateMemory.js";
import { HierarchicalReviewSurface } from "./HierarchicalReviewSurface.jsx";
import "./AnalysisExplorer.css";

const MONTHLY_REVIEW_MONTH_SESSION_KEY = "wallet-watch:analysis-monthly-review-month";
const MONTHLY_REVIEW_EXPANDED_SESSION_KEY = "wallet-watch:analysis-monthly-review-expanded";
const MONTHLY_REVIEW_SELECTION_SESSION_KEY = "wallet-watch:analysis-monthly-review-selection";
const monthLabelKeys = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

function buildBudgetCategoryKey(row) {
  if (row?.id) {
    return `id:${row.id}`;
  }

  return `name:${String(row?.category ?? "").trim()}`;
}

function buildExpenseCategoryKey(row) {
  if (row?.categoryId) {
    return `id:${row.categoryId}`;
  }

  return `name:${String(row?.category ?? "").trim()}`;
}

function parseExpenseDate(dateText) {
  if (!dateText) {
    return null;
  }

  const parsed = new Date(`${dateText}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function getSelectedMonthDateContext(baseContext, selectedMonthIndex) {
  const scopedYear = baseContext.scopedYear;
  const isCurrentMonth = baseContext.isCurrentYear && selectedMonthIndex === baseContext.currentMonthIndex;
  const selectedMonthDate = isCurrentMonth
    ? new Date(scopedYear, baseContext.currentMonthIndex, baseContext.currentDate)
    : new Date(scopedYear, selectedMonthIndex + 1, 0);

  return createScopeDateContext(scopedYear, selectedMonthDate);
}

function isInSelectedMonth(row, dateContext) {
  const parsedDate = parseExpenseDate(row.date);
  if (!parsedDate) {
    return false;
  }

  if (parsedDate.getFullYear() !== dateContext.scopedYear) {
    return false;
  }

  if (parsedDate.getMonth() !== dateContext.currentMonthIndex) {
    return false;
  }

  if (dateContext.isCurrentYear && parsedDate.getDate() > dateContext.currentDate) {
    return false;
  }

  return true;
}

function sumExpenseAmounts(rows = []) {
  return rows.reduce((sum, row) => sum + Number(row.amount ?? 0), 0);
}

function getExpenseSupplierLabel(row, t = (key) => key) {
  const label = String(row?.supplier ?? "").trim();
  return label || t("unlabeled");
}

function messageToneColor(tone) {
  if (tone === "bad") {
    return "var(--color-danger)";
  }

  if (tone === "caution") {
    return "var(--color-warning)";
  }

  return "var(--color-success)";
}

function buildMonthlyStatementRows(rows = [], expenseRows = [], dateContext, selectedMonthIndex, t = (key) => key) {
  const monthContext = getSelectedMonthDateContext(dateContext, selectedMonthIndex);
  const monthExpenseRows = expenseRows.filter((row) => isInSelectedMonth(row, monthContext));
  const monthOngoingRowsByCategory = monthExpenseRows.reduce((groups, row) => {
    if (normalizeExpenseType(row.type) !== EXPENSE_TYPE_ONGOING) {
      return groups;
    }

    const key = buildExpenseCategoryKey(row);
    const current = groups.get(key) ?? [];
    current.push(row);
    groups.set(key, current);
    return groups;
  }, new Map());
  const monthIrregularRowsByCategory = monthExpenseRows.reduce((groups, row) => {
    if (normalizeExpenseType(row.type) !== EXPENSE_TYPE_IRREGULAR) {
      return groups;
    }

    const key = buildExpenseCategoryKey(row);
    const current = groups.get(key) ?? [];
    current.push(row);
    groups.set(key, current);
    return groups;
  }, new Map());
  const statementRows = rows.map((row) => {
    const key = buildBudgetCategoryKey(row);
    const ongoingBudget = Number(row.ongoingBudget ?? 0);
    const irregularBudget = Number(row.irregularBudget ?? 0);
    const monthlyBudget = ongoingBudget + irregularBudget;
    const ongoingSpent = sumExpenseAmounts(monthOngoingRowsByCategory.get(key) ?? []);
    const irregularSpent = sumExpenseAmounts(monthIrregularRowsByCategory.get(key) ?? []);
    const irregularSpentYtd = expenseRows.reduce((sum, expenseRow) => {
      if (buildExpenseCategoryKey(expenseRow) !== key) {
        return sum;
      }

      if (normalizeExpenseType(expenseRow.type) !== EXPENSE_TYPE_IRREGULAR) {
        return sum;
      }

      const parsedDate = parseExpenseDate(expenseRow.date);
      if (!parsedDate || parsedDate.getFullYear() !== monthContext.scopedYear) {
        return sum;
      }

      if (parsedDate.getMonth() > monthContext.currentMonthIndex) {
        return sum;
      }

      return sum + Number(expenseRow.amount ?? 0);
    }, 0);
    const ongoingRemaining = Math.max(ongoingBudget - ongoingSpent, 0);
    const ongoingProjectedRows = monthOngoingRowsByCategory.get(key) ?? [];
    const ongoingImplied = ongoingProjectedRows.length > 0
      ? sumExpenseAmounts(ongoingProjectedRows)
      : 0;
    const projectedSpend = Math.max(ongoingSpent, ongoingImplied);
    const totalSpent = ongoingSpent + irregularSpent;
    const hasExpected = ongoingSpent === 0 && ongoingRemaining > 0;
    const expectedAmount = hasExpected ? ongoingRemaining : 0;
    const ongoingTone = ongoingSpent > ongoingBudget ? "bad" : "good";
    const irregularTone = irregularSpentYtd > irregularBudget ? "bad" : "good";
    const categoryExpenseRows = expenseRows.filter((expenseRow) => buildExpenseCategoryKey(expenseRow) === key);
    const supplierRows = Array.from(categoryExpenseRows.reduce((groups, expenseRow) => {
      const supplier = getExpenseSupplierLabel(expenseRow, t);
      const current = groups.get(supplier) ?? [];
      current.push(expenseRow);
      groups.set(supplier, current);
      return groups;
    }, new Map()).entries())
      .map(([supplier, supplierExpenseRows]) => {
        const supplierOngoingSpent = sumExpenseAmounts(
          supplierExpenseRows.filter((expenseRow) => (
            normalizeExpenseType(expenseRow.type) === EXPENSE_TYPE_ONGOING
            && isInSelectedMonth(expenseRow, monthContext)
          ))
        );
        const supplierIrregularSpent = sumExpenseAmounts(
          supplierExpenseRows.filter((expenseRow) => (
            normalizeExpenseType(expenseRow.type) === EXPENSE_TYPE_IRREGULAR
            && isInSelectedMonth(expenseRow, monthContext)
          ))
        );
        const supplierTotalSpent = supplierOngoingSpent + supplierIrregularSpent;

        return {
          id: `${key}|supplier:${supplier.toLocaleLowerCase()}`,
          label: supplier,
          ongoingBudget: null,
          irregularBudget: null,
          ongoingSpent: supplierOngoingSpent,
          irregularSpent: supplierIrregularSpent,
          ongoingRemaining: 0,
          ongoingImplied: 0,
          projectedSpend: supplierOngoingSpent,
          totalSpent: supplierTotalSpent,
          hasExpected: false,
          expectedAmount: 0,
          messages: [
            supplierOngoingSpent > 0 ? {
              key: "spent",
              tone: ongoingTone,
              value: formatCurrency(supplierOngoingSpent),
              label: t("ongoingSpend")
            } : null,
            supplierIrregularSpent > 0 ? {
              key: "irregular-spent",
              tone: irregularTone,
              value: formatCurrency(supplierIrregularSpent),
              label: t("irregularSpent")
            } : null
          ].filter(Boolean),
          children: []
        };
      })
      .filter((supplierRow) => supplierRow.totalSpent > 0)
      .sort((left, right) => (
        right.totalSpent - left.totalSpent
        || left.label.localeCompare(right.label)
      ));

    return {
      id: key,
      label: row.category,
      category: row.category,
      monthlyBudget,
      ongoingBudget,
      irregularBudget,
      ongoingSpent,
      irregularSpent,
      irregularSpentYtd,
      ongoingRemaining,
      ongoingImplied,
      projectedSpend,
      totalSpent,
      hasExpected,
      expectedAmount,
      messages: [
        ongoingSpent > 0 ? {
          key: "spent",
          tone: ongoingSpent > ongoingBudget ? "bad" : "good",
          value: formatCurrency(ongoingSpent),
          label: t("ongoingSpend")
        } : null,
        irregularSpent > 0 ? {
          key: "irregular-spent",
          tone: irregularSpentYtd > irregularBudget ? "bad" : "good",
          value: formatCurrency(irregularSpent),
          label: t("irregularSpent")
        } : null,
        ongoingRemaining > 0 ? {
          key: "remaining",
          tone: "caution",
          value: formatCurrency(ongoingRemaining),
          label: ongoingSpent > 0 ? t("remaining") : t("expected")
        } : null
      ].filter(Boolean),
      children: supplierRows
    };
  });

  return statementRows
    .filter((row) => row.totalSpent > 0 || row.hasExpected || row.irregularSpent > 0)
    .sort((left, right) => (
      right.totalSpent - left.totalSpent
      || Number(right.hasExpected) - Number(left.hasExpected)
      || right.expectedAmount - left.expectedAmount
      || left.label.localeCompare(right.label)
    ));
}

function buildMonthlyStatementTotalRow(statementRows = [], t = (key) => key) {
  const total = statementRows.reduce((accumulator, row) => ({
    ongoingBudget: accumulator.ongoingBudget + Number(row.ongoingBudget ?? 0),
    irregularBudget: accumulator.irregularBudget + Number(row.irregularBudget ?? 0),
    ongoingSpent: accumulator.ongoingSpent + Number(row.ongoingSpent ?? 0),
    irregularSpent: accumulator.irregularSpent + Number(row.irregularSpent ?? 0),
    irregularSpentYtd: accumulator.irregularSpentYtd + Number(row.irregularSpentYtd ?? 0),
    ongoingRemaining: accumulator.ongoingRemaining + Number(row.ongoingRemaining ?? 0)
  }), {
    ongoingBudget: 0,
    irregularBudget: 0,
    ongoingSpent: 0,
    irregularSpent: 0,
    irregularSpentYtd: 0,
    ongoingRemaining: 0
  });

  return {
    label: t("total"),
    messages: [
      total.ongoingSpent > 0 ? {
        key: "spent",
        tone: total.ongoingSpent > total.ongoingBudget ? "bad" : "good",
        value: formatCurrency(total.ongoingSpent),
        label: t("ongoingSpend")
      } : null,
      total.irregularSpent > 0 ? {
        key: "irregular-spent",
        tone: total.irregularSpentYtd > total.irregularBudget ? "bad" : "good",
        value: formatCurrency(total.irregularSpent),
        label: t("irregularSpent")
      } : null,
      total.ongoingRemaining > 0 ? {
        key: "remaining",
        tone: "caution",
        value: formatCurrency(total.ongoingRemaining),
        label: total.ongoingSpent > 0 ? t("remaining") : t("expected")
      } : null
    ].filter(Boolean)
  };
}

function flattenStatementRows(nodes, expandedIds, depth = 0) {
  return nodes.flatMap((node) => {
    const expanded = expandedIds.has(node.id);
    const currentRow = [{
      ...node,
      depth,
      expanded
    }];

    if (!node.children?.length || !expanded) {
      return currentRow;
    }

    return currentRow.concat(flattenStatementRows(node.children, expandedIds, depth + 1));
  });
}

function StatementMessageCell({ message = null }) {
  if (!message) {
    return null;
  }

  return (
    <>
      <strong style={{ color: messageToneColor(message.tone) }}>{message.value}</strong>
      <span> {message.label}</span>
    </>
  );
}

function StatementRow({
  row,
  rowIndex,
  isSelected,
  labelRef,
  onSelect,
  onToggle
}) {
  if (row.detail) {
    return (
      <tr
        data-row-index={rowIndex}
        class={[
          "analysis-explorer-row",
          "analysis-explorer-row--detail",
          isSelected ? "analysis-explorer-row--selected" : ""
        ].filter(Boolean).join(" ")}
        onClick={() => onSelect(row.id)}
      >
        <td class="analysis-explorer-cell align-start">
          <span class="analysis-explorer-label-text" ref={isSelected ? labelRef : null}>{row.label}</span>
        </td>
        <td class="analysis-explorer-cell align-start" colSpan="5">
          {row.detailText}
        </td>
      </tr>
    );
  }

  const hasChildren = Boolean(row.children?.length);

  return (
    <tr
      data-row-index={rowIndex}
      class={[
        "analysis-explorer-row",
        hasChildren ? "analysis-explorer-row--branch" : "",
        hasChildren && row.expanded ? "analysis-explorer-row--expanded" : "",
        isSelected ? "analysis-explorer-row--selected" : ""
      ].filter(Boolean).join(" ")}
      onClick={() => {
        onSelect(row.id);
        if (hasChildren) {
          onToggle(row.id);
        }
      }}
    >
      <td class="analysis-explorer-cell align-start">
        <div class="analysis-explorer-label-wrap" style={{ "--analysis-node-depth": row.depth }}>
          {hasChildren ? (
            <button
              class="analysis-explorer-toggle"
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onToggle(row.id);
              }}
            >
              {row.expanded ? "-" : "+"}
            </button>
          ) : (
            <span class="analysis-explorer-toggle-placeholder" />
          )}
          <span class="analysis-explorer-label-text" ref={isSelected ? labelRef : null}>{row.label}</span>
        </div>
      </td>
      {Array.from({ length: 3 }, (_, index) => row.messages[index] ?? null).map((message, index) => (
        <td key={message?.key ?? `empty-${index}`} class="analysis-explorer-cell align-start">
          {message ? <StatementMessageCell message={message} /> : "\u00a0"}
        </td>
      ))}
    </tr>
  );
}

function StatementHeaderRow({ row, headerClassName = "", headerCellClassName = "" }) {
  return (
    <thead class={headerClassName}>
      <tr class="monthly-review-total-row">
        <th scope="col" class={[headerCellClassName, "align-start"].filter(Boolean).join(" ")}>
        <div class="analysis-explorer-label-wrap" style={{ "--analysis-node-depth": 0 }}>
          <span class="analysis-explorer-toggle-placeholder" />
          <span class="analysis-explorer-label-text">{row.label}</span>
        </div>
        </th>
      {Array.from({ length: 3 }, (_, index) => row.messages[index] ?? null).map((message, index) => (
        <th
          key={message?.key ?? `empty-${index}`}
          scope="col"
          class={[headerCellClassName, "align-start"].filter(Boolean).join(" ")}
        >
          {message ? <StatementMessageCell message={message} /> : "\u00a0"}
        </th>
      ))}
      </tr>
    </thead>
  );
}

function deserializeExpandedIds(storedValue) {
  try {
    const parsed = JSON.parse(storedValue);
    return new Set(Array.isArray(parsed) ? parsed.filter((value) => typeof value === "string") : []);
  } catch {
    return new Set();
  }
}

function serializeExpandedIds(value) {
  return JSON.stringify(Array.from(value ?? []));
}

export function MonthlyReview({
  rows = [],
  expenseRows = [],
  dateContext = null,
  onNavigateToPage,
  showAltHints = false,
  t = (key) => key
}) {
  const reviewMonthContext = dateContext ?? createScopeDateContext(undefined, new Date());
  const monthOptions = useMemo(() => monthLabelKeys.map((monthKey, monthIndex) => ({
    value: monthIndex,
    label: t(monthKey)
  })), [t]);
  const defaultMonthIndex = Math.min(Math.max(reviewMonthContext.currentMonthIndex, 0), 11);
  const statementSurfaceRef = useRef(null);
  const monthControlRef = useRef(null);
  const [selectedMonthIndex, setSelectedMonthIndex] = usePageStateMemory(
    MONTHLY_REVIEW_MONTH_SESSION_KEY,
    defaultMonthIndex,
    {
      deserialize(storedValue) {
        const parsed = Number.parseInt(storedValue, 10);
        return Number.isInteger(parsed) && parsed >= 0 && parsed <= 11 ? parsed : defaultMonthIndex;
      },
      serialize(value) {
        return String(value ?? defaultMonthIndex);
      }
    }
  );
  const [expandedIds, setExpandedIds] = usePageStateMemory(
    MONTHLY_REVIEW_EXPANDED_SESSION_KEY,
    new Set(),
    {
      deserialize: deserializeExpandedIds,
      serialize: serializeExpandedIds
    }
  );
  const [selectedRowId, setSelectedRowId] = usePageStateMemory(
    MONTHLY_REVIEW_SELECTION_SESSION_KEY,
    null,
    {
      deserialize(storedValue) {
        return storedValue ? String(storedValue) : null;
      },
      serialize(value) {
        return value ? String(value) : null;
      }
    }
  );
  const activeMonthIndex = monthOptions.some((option) => option.value === selectedMonthIndex)
    ? selectedMonthIndex
    : defaultMonthIndex;
  const statementRows = useMemo(
    () => buildMonthlyStatementRows(rows, expenseRows, reviewMonthContext, activeMonthIndex, t),
    [activeMonthIndex, expenseRows, reviewMonthContext, rows, t]
  );
  const flattenedRows = useMemo(
    () => flattenStatementRows(statementRows, expandedIds),
    [expandedIds, statementRows]
  );
  const pinnedRow = useMemo(
    () => buildMonthlyStatementTotalRow(statementRows, t),
    [statementRows, t]
  );
  const toolbarActionsForRow = useCallback((row) => {
    if (!row) {
      return [];
    }

    if (row.depth === 0) {
      return [
        {
          key: "show-in-expenses",
          label: t("expenses"),
          onSelect: () => {
            setExpensesSearchQuery(row.label);
            onNavigateToPage?.("expenses");
          }
        },
        {
          key: "show-in-budget",
          label: t("budget"),
          onSelect: () => {
            setBudgetSearchQuery(row.label);
            onNavigateToPage?.("budget");
          }
        },
        {
          key: "show-in-chart",
          label: t("chart"),
          onSelect: () => {
            setAnalysisChartCategory(row.label);
            onNavigateToPage?.("analysis", "charts");
          }
        }
      ];
    }

    if (row.depth === 1) {
      return [
        {
          key: "show-in-expenses",
          label: t("expenses"),
          onSelect: () => {
            setExpensesSearchQuery(row.label);
            onNavigateToPage?.("expenses");
          }
        },
        {
          key: "show-in-chart",
          label: t("chart"),
          onSelect: () => {
            setAnalysisChartSupplier(row.label);
            onNavigateToPage?.("analysis", "charts");
          }
        }
      ];
    }

    return [];
  }, [onNavigateToPage, t]);
  const pageBindings = useMemo(() => [
    ...createSectionNavigationBindings({
      options: monthOptions,
      value: activeMonthIndex,
      onChange: setSelectedMonthIndex
    }),
    onKey("Alt+E", () => {
      const activeButton = monthControlRef.current?.querySelector(".segmented-control-option.active");
      const fallbackButton = monthControlRef.current?.querySelector(".segmented-control-option");
      (activeButton ?? fallbackButton)?.focus();
    }, { allowInInput: true }),
    onKey("Alt+W", () => {
      statementSurfaceRef.current?.focus({ preventScroll: true });
    }, { allowInInput: true })
  ], [activeMonthIndex, monthOptions, setSelectedMonthIndex]);
  useShortcutBindings(window, pageBindings);

  useEffect(() => {
    statementSurfaceRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    if (flattenedRows.length === 0) {
      if (selectedRowId !== null) {
        setSelectedRowId(null);
      }
      return;
    }

    if (flattenedRows.some((row) => row.id === selectedRowId)) {
      return;
    }

    setSelectedRowId(flattenedRows[0].id);
  }, [flattenedRows, selectedRowId, setSelectedRowId]);

  function handleToggleRow(rowId) {
    setExpandedIds((current) => {
      const nextExpanded = new Set(current);
      if (nextExpanded.has(rowId)) {
        nextExpanded.delete(rowId);
      } else {
        nextExpanded.add(rowId);
      }

      return nextExpanded;
    });
  }

  return (
    <div class="monthly-review-page fill-area">
      <Panel
        className="analysis-explorer-panel fill-area"
        toolbar={(
          <ControlBar
            boxed={false}
            start={(
              <AltHintTarget label="E" visible={showAltHints} targetRef={monthControlRef}>
                <SegmentedControl
                  options={monthOptions}
                  value={activeMonthIndex}
                  onChange={setSelectedMonthIndex}
                />
              </AltHintTarget>
            )}
          />
        )}
        scroll
      >
        <HierarchicalReviewSurface
          rows={flattenedRows}
          selectedRowId={selectedRowId}
          onSelectedRowIdChange={setSelectedRowId}
          onToggleRow={handleToggleRow}
          renderHeader={({ headerClassName, headerCellClassName }) => (
            <StatementHeaderRow
              row={pinnedRow}
              headerClassName={headerClassName}
              headerCellClassName={headerCellClassName}
            />
          )}
          headerVariant="plain"
          renderRow={({ row, rowIndex, isSelected, labelRef, onSelect, onToggle }) => (
            <StatementRow
              key={row.id}
              row={row}
              rowIndex={rowIndex}
              isSelected={isSelected}
              labelRef={labelRef}
              onSelect={onSelect}
              onToggle={onToggle}
            />
          )}
          emptyContent={t("noExpenseDataYetForExplorer")}
          baseClassName="analysis-explorer"
          dataRole="monthly-statement-grid"
          surfaceRef={statementSurfaceRef}
          showAltHints={showAltHints}
          altHintLabel="W"
          toolbarPlacement="inline-end"
          toolbarOffset={24}
          getToolbarActions={toolbarActionsForRow}
        />
      </Panel>
    </div>
  );
}
